<script lang="ts">
  import { session } from '$lib/stores';
  import type { auctions, bids, lots, users } from '@prisma/client';
  import CloudflareImage from './CloudflareImage.svelte';
  import Countdown from './Countdown.svelte';
  import LotPrice from './lots/LotPrice.svelte';
  import UnpublishedAlert from './UnpublishedAlert.svelte';
  import { writable } from 'svelte/store';
  import { invalidateAll } from '$app/navigation';
  import { onMount } from 'svelte';
  import { shouldLotBeUpdated } from '$lib/utils';
  import AwardedBadge from './lots/AwardedBadge.svelte';

  type IUser = Pick<users, 'uid'>;
  type IBid = Pick<bids, 'net_amount'> & {
    awarded?: bids['awarded'];
    user: IUser;
  };
  type IAuction = Pick<auctions, 'type'>;
  type ILot = Pick<
    lots,
    | 'uid'
    | 'nr'
    | 'description'
    | 'base_price'
    | 'status'
    | 'images'
    | 'end_date'
    | 'published'
    | 'base_price'
    | 'end_date'
  > & {
    auction: IAuction;
    bids?: IBid[];
  };

  export let lot: ILot & { bids: IBid[] };

  const latestBid = writable<IBid | null>(lot.bids?.[0] ?? null);
  const sold = writable<boolean>(false);
  $: {
    latestBid.set(lot.bids?.[0] ?? null);
    sold.set(lot.bids?.some((b) => b.awarded) ?? false);
  }

  onMount(() => shouldLotBeUpdated(lot, invalidateAll));
</script>

<div data-lot-id={`lot:${lot.uid}`} class="col-6 col-md-4 col-xl-3">
  <div
    class="group relative bg-white border border-gray-200 rounded-lg flex flex-col overflow-hidden"
    class:opacity-75={$sold}
  >
    <div>
      <div class="aspect-w-3 aspect-h-4 bg-gray-200 group-hover:opacity-75 sm:aspect-none sm:h-96">
        <CloudflareImage
          src={lot.images}
          class="w-full h-full object-center object-cover sm:w-full sm:h-full"
          alt={lot.description}
        />
      </div>
      {#if $sold}
        <AwardedBadge />
      {/if}
    </div>
    <div class="flex-1 p-4 space-y-2 flex flex-col">
      <h3 class="text-sm font-medium text-gray-900">
        <a href={`/lotes/lote-${lot.nr}/${lot.uid}`}>
          <span aria-hidden="true" class="absolute inset-0" />
          Lote Nro. {lot.nr}
        </a>
      </h3>
      <p class="text-sm text-gray-500">{lot.description}</p>
      {#if lot.status == 'ongoing' && lot.end_date && lot.auction.type != 'face_to_face'}
        <div class="card-countdown mt-2">
          <Countdown end_date={lot.end_date} onFinished={() => {}} />
        </div>
      {/if}
      <div class="flex-1 flex flex-row justify-end">
        <div class="text-base font-medium text-gray-900 flex flex-row w-full">
          <LotPrice {lot} class="text-m sm:text-m" />
        </div>
      </div>
      {#if lot.status == 'ongoing' && (lot.auction.type == 'live_with_preoffers' || lot.auction.type == 'timed')}
        {#if $session && $latestBid?.user?.uid == $session.uid}
          <span
            class="relative mt-10 z-0 justify-center flex shadow-sm rounded-md bg-green-300 text-sm font-medium text-green-600 px-4 py-2"
          >
            Estas ganando esta subasta
          </span>
        {:else}
          <a
            href={`/lotes/lote-${lot.nr}/${lot.uid}`}
            class="
            w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm
            font-medium text-white bg-primary-500 hover:bg-primary-400 focus:outline-none focus:ring-2
            focus:ring-offset-2 focus:ring-primary-500 mt-4
          "
          >
            Haga su oferta
          </a>
        {/if}
      {/if}
      {#if !lot.published}<UnpublishedAlert />{/if}
    </div>
  </div>
</div>
